import { useTranslation } from 'react-i18next';
import ReactMarkdown from 'react-markdown';

interface ContentBlockProps {
  title: string;
  content: string;
  type: 'definition' | 'example' | 'analogy' | 'summary';
  accentColor: string;
  nodeRef?: string;
  depth?: 'full' | 'condensed' | 'minimal';
}

const TYPE_STYLES: Record<string, {
  icon: string;
  borderClass: string;
  bgClass: string;
  labelColor: string;
}> = {
  definition: {
    icon: '📖',
    borderClass: 'border-l-4 border-l-amber-500',
    bgClass: 'bg-gray-800',
    labelColor: 'text-amber-400',
  },
  example: {
    icon: '💡',
    borderClass: 'border-l-4 border-l-emerald-500',
    bgClass: 'bg-gray-800',
    labelColor: 'text-emerald-400',
  },
  analogy: {
    icon: '🌐',
    borderClass: 'border-l-4 border-l-violet-500',
    bgClass: 'bg-gray-800',
    labelColor: 'text-violet-400',
  },
  summary: {
    icon: '📋',
    borderClass: 'border border-amber-500/20',
    bgClass: 'bg-amber-500/5',
    labelColor: 'text-amber-400',
  },
};

export default function ContentBlock({ title, content, type, accentColor, depth }: ContentBlockProps) {
  const { t } = useTranslation('session');
  const styles = TYPE_STYLES[type] || TYPE_STYLES.definition;

  // Summary uses session accent color instead of fixed amber
  const ACCENT_LABEL: Record<string, string> = {
    amber: 'text-amber-400',
    blue: 'text-blue-400',
    indigo: 'text-indigo-400',
  };
  const ACCENT_BORDER: Record<string, string> = {
    amber: 'border-amber-500/20',
    blue: 'border-blue-500/20',
    indigo: 'border-indigo-500/20',
  };
  const ACCENT_BG: Record<string, string> = {
    amber: 'bg-amber-500/5',
    blue: 'bg-blue-500/5',
    indigo: 'bg-indigo-500/5',
  };

  const labelColor = type === 'summary'
    ? (ACCENT_LABEL[accentColor] || styles.labelColor)
    : styles.labelColor;
  const borderClass = type === 'summary'
    ? `border ${ACCENT_BORDER[accentColor] || 'border-amber-500/20'}`
    : styles.borderClass;
  const bgClass = type === 'summary'
    ? (ACCENT_BG[accentColor] || styles.bgClass)
    : styles.bgClass;

  // Minimal depth: compact chip with icon and title only
  if (depth === 'minimal') {
    return (
      <div className={`px-3 py-1.5 rounded-md border ${borderClass.replace('border-l-4 ', '')} ${bgClass} inline-flex items-center gap-1.5`}>
        <span className="text-xs">{styles.icon}</span>
        <span className={`text-xs font-medium ${labelColor}`}>{title || t(`contentBlock.types.${type}`)}</span>
      </div>
    );
  }

  // Condensed depth: title + first paragraph only
  const displayContent = depth === 'condensed'
    ? content.split('\n\n')[0]
    : content;

  return (
    <div
      className={`rounded-xl ${borderClass} ${bgClass} p-4 animate-fade-in-simple`}
    >
      <div className="flex items-center gap-2 mb-2">
        <span className="text-base">{styles.icon}</span>
        <span className={`text-xs font-semibold uppercase tracking-wide ${labelColor}`}>
          {t(`contentBlock.types.${type}`)}
        </span>
      </div>

      <h3 className="text-sm font-semibold text-gray-200 mb-1">{title}</h3>

      <div className="text-sm text-gray-300 leading-relaxed prose prose-invert prose-sm max-w-none">
        <ReactMarkdown>{displayContent}</ReactMarkdown>
      </div>
    </div>
  );
}
